<template>
  <div ref="root" class="date-range-filter position-relative d-block">
    <!-- Trigger Button (looks like other inputs) -->
    <b-button
      variant="outline-primary"
      block
      class="d-flex align-items-center justify-content-start"
      @click="togglePopup"
    >
      <feather-icon icon="CalendarIcon" size="16" class="mr-50" />
      <span>{{ buttonLabel }}</span>
    </b-button>

    <!-- Popup -->
    <transition name="fade">
      <div v-if="isOpen" class="date-range-popup shadow-lg" @click.stop>
        <!-- Date type toggle -->
        <div class="date-type-toggle mb-1 d-flex justify-content-between align-items-center">
          <div>
            <b-button
              size="sm"
              :variant="activeType === 'creation' ? 'primary' : 'outline-secondary'"
              class="mr-50"
              @click="activeType = 'creation'"
            >
              {{ isTurkish ? 'Sevkiyat Oluşturma' : 'Shipment Creation' }}
            </b-button>
            <b-button
              size="sm"
              :variant="activeType === 'acceptance' ? 'primary' : 'outline-secondary'"
              @click="activeType = 'acceptance'"
            >
              {{ isTurkish ? 'Sevkiyat Kabul' : 'Shipment Acceptance' }}
            </b-button>
          </div>
          <feather-icon icon="XIcon" class="cursor-pointer" @click="closePopup" />
        </div>

        <!-- Quick range select -->
        <b-form-group class="mb-1">
          <b-form-select v-model="quickRange" :options="quickRangeOptions" size="sm" />
        </b-form-group>

        <p class="small mb-50">
          {{
            isTurkish
              ? 'Aşağıdan özel bir tarih aralığı seçin.'
              : 'Select a custom date range below.'
          }}
        </p>

        <!-- Date range input (opens calendar on focus) -->
        <b-input-group class="mb-1">
          <flat-pickr
            :key="`flatpickr-${quickRange}`"
            v-model="calendarRange"
            :config="calendarConfig"
            class="form-control date-range-input"
            :readonly="isInputReadonly"
            :placeholder="
              currentRange.start && currentRange.end
                ? formatDisplayRange(currentRange.start, currentRange.end)
                : isTurkish
                ? 'Başlangıç ve bitiş tarihi seçin'
                : 'Select start and end date'
            "
          />
        </b-input-group>

        <!-- Actions -->
        <div class="d-flex justify-content-between align-items-center mt-1">
          <b-button variant="link" class="p-0 text-danger" @click="handleClear">
            {{ $t('Clear') }}
          </b-button>
          <b-button variant="primary" size="sm" @click="handleApply">
            {{ $t('Apply') }}
          </b-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { BButton, BFormGroup, BFormSelect, BInputGroup } from 'bootstrap-vue';
import flatPickr from 'vue-flatpickr-component';
import { Turkish } from 'flatpickr/dist/l10n/tr.js';
import englishLocale from 'flatpickr/dist/l10n/default.js';

export default {
  name: 'DateRangeFilter',

  components: {
    BButton,
    BFormGroup,
    BFormSelect,
    BInputGroup,
    flatPickr
  },

  props: {
    /**
     * value shape:
     * {
     *   dateFrom: '',
     *   dateTo: '',
     *   dropOffDateFrom: '',
     *   dropOffDateTo: ''
     * }
     */
    value: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      isOpen: false,
      activeType: 'creation', // 'creation' | 'acceptance'
      previousActiveType: 'creation', // Önceki tab'ı takip etmek için
      quickRange: 'custom',
      ranges: {
        creation: { start: '', end: '' },
        acceptance: { start: '', end: '' }
      },
      calendarRange: ''
    };
  },

  computed: {
    isTurkish() {
      const locale = (this.$i18n && this.$i18n.locale) || 'tr';
      return String(locale).toLowerCase().startsWith('tr');
    },

    buttonLabel() {
      if (this.currentRange.start && this.currentRange.end) {
        return this.formatDisplayRange(this.currentRange.start, this.currentRange.end);
      }
      return this.$t('Select Date');
    },

    quickRangeOptions() {
      return [
        { value: 'custom', text: this.isTurkish ? 'Belirli Aralık' : 'Custom Range' },
        { value: 'yesterday', text: this.isTurkish ? 'Dün' : 'Yesterday' },
        { value: 'last3days', text: this.isTurkish ? 'Son 3 Gün' : 'Last 3 Days' },
        { value: 'lastWeek', text: this.isTurkish ? 'Geçen Hafta' : 'Last Week' },
        { value: 'lastMonth', text: this.isTurkish ? 'Geçen Ay' : 'Last Month' }
      ];
    },

    currentRange() {
      return this.activeType === 'acceptance' ? this.ranges.acceptance : this.ranges.creation;
    },

    flatpickrLocale() {
      return this.isTurkish ? Turkish : englishLocale;
    },

    rangeSeparator() {
      return this.flatpickrLocale.rangeSeparator || (this.isTurkish ? ' - ' : ' to ');
    },

    calendarConfig() {
      return {
        mode: 'range',
        dateFormat: 'Y-m-d',
        inline: false,
        enableTime: false,
        altInput: true,
        altFormat: 'd-m-Y',
        locale: this.flatpickrLocale,
        clickOpens: this.quickRange === 'custom',
        allowInput: this.quickRange === 'custom'
      };
    },

    isInputReadonly() {
      return this.quickRange !== 'custom';
    }
  },

  watch: {
    value: {
      handler(newVal) {
        this.syncFromValue(newVal || {});
      },
      deep: true,
      immediate: true
    },

    activeType(newType) {
      // Tab değiştiğinde, önceki tab'ın tarihlerini temizle
      if (this.previousActiveType && this.previousActiveType !== newType) {
        const previousRange =
          this.previousActiveType === 'acceptance' ? this.ranges.acceptance : this.ranges.creation;
        previousRange.start = '';
        previousRange.end = '';
      }

      // Aktif tab için input'ları sıfırla
      this.quickRange = 'custom';
      this.currentRange.start = '';
      this.currentRange.end = '';
      this.calendarRange = '';
      this.syncCalendarFromActiveType();

      // Önceki tab'ı güncelle
      this.previousActiveType = newType;

      // Tarih değişikliklerini emit et
      this.emitChange();
    },

    quickRange(newVal) {
      if (newVal && newVal !== 'custom') {
        this.applyQuickRange(newVal);
      } else if (newVal === 'custom') {
        // Clear dates when switching to custom
        this.currentRange.start = '';
        this.currentRange.end = '';
        this.calendarRange = '';
      }
    },

    calendarRange(newVal) {
      if (!newVal) {
        this.currentRange.start = '';
        this.currentRange.end = '';
        return;
      }

      const parts = newVal.split(this.rangeSeparator);
      this.currentRange.start = parts[0] || '';
      this.currentRange.end = parts[1] || parts[0] || '';
    }
  },

  methods: {
    handleClickOutside(event) {
      if (!this.isOpen) return;
      if (event.target.closest && event.target.closest('.flatpickr-calendar')) {
        return;
      }
      if (this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.closePopup();
      }
    },

    togglePopup() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.syncCalendarFromActiveType();
      }
    },

    closePopup() {
      this.isOpen = false;
    },

    syncFromValue(val) {
      this.ranges.creation.start = val.dateFrom || '';
      this.ranges.creation.end = val.dateTo || '';
      this.ranges.acceptance.start = val.dropOffDateFrom || '';
      this.ranges.acceptance.end = val.dropOffDateTo || '';
      this.syncCalendarFromActiveType();
    },

    syncCalendarFromActiveType() {
      const { start, end } = this.currentRange;
      if (start && end) {
        this.calendarRange = `${start}${this.rangeSeparator}${end}`;
      } else {
        this.calendarRange = '';
      }
    },

    applyQuickRange(type) {
      const today = new Date();
      let start;
      let end;

      const format = date => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      switch (type) {
        case 'yesterday':
          end = new Date(today);
          end.setDate(end.getDate() - 1);
          start = new Date(end);
          break;
        case 'last3days':
          end = new Date(today);
          end.setDate(end.getDate() - 1);
          start = new Date(end);
          start.setDate(start.getDate() - 2);
          break;
        case 'lastWeek': {
          const dayOfWeek = today.getDay() || 7; // 1-7 (Mon-Sun)
          end = new Date(today);
          end.setDate(end.getDate() - dayOfWeek);
          start = new Date(end);
          start.setDate(start.getDate() - 6);
          break;
        }
        case 'lastMonth': {
          const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          end = new Date(firstOfThisMonth - 1); // last day of previous month
          start = new Date(end.getFullYear(), end.getMonth(), 1);
          break;
        }
        default:
          return;
      }

      const startStr = format(start);
      const endStr = format(end);

      this.currentRange.start = startStr;
      this.currentRange.end = endStr;
      this.calendarRange = `${startStr}${this.rangeSeparator}${endStr}`;
    },

    handleClear() {
      this.ranges.creation = { start: '', end: '' };
      this.ranges.acceptance = { start: '', end: '' };
      this.calendarRange = '';
      this.quickRange = 'custom';
      this.emitChange();
    },

    handleApply() {
      this.emitChange();
      this.closePopup();
    },

    emitChange() {
      const payload = {
        dateFrom: this.ranges.creation.start || '',
        dateTo: this.ranges.creation.end || '',
        dropOffDateFrom: this.ranges.acceptance.start || '',
        dropOffDateTo: this.ranges.acceptance.end || ''
      };
      this.$emit('input', payload);
    },

    formatDisplay(dateStr) {
      if (!dateStr) return '';
      const parts = dateStr.split('-');
      if (parts.length !== 3) return dateStr;
      const [year, month, day] = parts;
      return `${day}-${month}-${year}`;
    },

    formatDisplayRange(start, end) {
      return `${this.formatDisplay(start)} - ${this.formatDisplay(end)}`;
    }
  },

  mounted() {
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside);
  }
};
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.date-range-filter {
  .date-range-popup {
    position: absolute;
    top: calc(100% + 0.5rem);
    left: 0;
    z-index: 1050;
    width: 380px;
    border-radius: 0.5rem;
    border: 1px solid #ebe9f1;
    padding: 1rem;
    background-color: #fff;
    color: $body-color;

    .dark-layout & {
      background-color: $theme-dark-card-bg;
      border-color: $theme-dark-border-color;
      color: $theme-dark-body-color;
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
    }
  }

  .btn {
    font-size: $input-font-size;
    font-weight: 400;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.date-range-input {
  &::placeholder {
    color: #b5b7be !important;
  }

  &::-webkit-input-placeholder {
    color: #b5b7be !important;
  }

  &::-moz-placeholder {
    color: #b5b7be !important;
  }

  &:-ms-input-placeholder {
    color: #b5b7be !important;
  }

  &:-moz-placeholder {
    color: #b5b7be !important;
  }
}

::v-deep .flatpickr-input {
  color: #b5b7be !important;

  &::placeholder {
    color: #b5b7be !important;
  }

  &::-webkit-input-placeholder {
    color: #b5b7be !important;
  }

  &::-moz-placeholder {
    color: #b5b7be !important;
  }

  &:-ms-input-placeholder {
    color: #b5b7be !important;
  }

  &:-moz-placeholder {
    color: #b5b7be !important;
  }
}

::v-deep .flatpickr-input[readonly] {
  cursor: not-allowed;
  background-color: #f5f5f5;
}

::v-deep .flatpickr-altInput {
  color: #b5b7be !important;

  &::placeholder {
    color: #b5b7be !important;
  }

  &::-webkit-input-placeholder {
    color: #b5b7be !important;
  }

  &::-moz-placeholder {
    color: #b5b7be !important;
  }

  &:-ms-input-placeholder {
    color: #b5b7be !important;
  }

  &:-moz-placeholder {
    color: #b5b7be !important;
  }
}
</style>
